/**
 * Confidence and learning (spec §2 I) and lifecycle (§2 L).
 *
 * Learned rules are the only memory that crosses runs: written in plain words
 * by the nightly job or a person, entering the prompt only when a person
 * approves, reverted in one click. With the gold-set gate on, a draft is
 * checked against recent decisions people accepted before it can be approved.
 * Calibration shows what each stated confidence has meant here. Every output
 * is a decision record.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, Plus } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Callout, DataTable, type Column } from '../../ds';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { loadAgentCalibration, rebuildAgentCalibration, MIN_REVIEWS_FOR_CALIBRATION, type CalibrationRow } from '../../../lib/agentCalibrationService';
import { approveLearnedRule, dismissLearnedRule, draftLearnedRule, loadLearnedRules, revertLearnedRule, type LearnedRule } from '../../../lib/agentLearnedRulesService';
import { checkRuleAgainstGoldSet, DEFAULT_GOLD_SET_SIZE, type RegressionCheck } from '../../../lib/agentRegressionGate';
import { proposedWords, reviewOutcomeWords } from '../outputs/types';
import { knownActorName, useActorDisplayMap } from '../../../hooks/prioritization/useActorDisplayMap';
import { SwitchGroup, SwitchRow, type SectionProps } from './shared';

const CALIBRATION_COLUMNS: Column<CalibrationRow>[] = [
  { key: 'field', header: 'Attribute', render: (r) => <span className="font-medium text-[var(--text-pri)]">{r.field}</span> },
  { key: 'bucket', header: 'Stated', render: (r) => r.bucket },
  { key: 'reviewed', header: 'Reviewed', render: (r) => String(r.reviewedCount - r.expiredCount) },
  { key: 'rate', header: 'Accepted unchanged', render: (r) => (r.acceptedRate == null ? '—' : `${Math.round(r.acceptedRate * 100)}%`) },
  { key: 'changed', header: 'Changed / rejected / reversed', render: (r) => `${r.acceptedWithChangeCount} / ${r.rejectedCount} / ${r.reversedCount}` },
  { key: 'error', header: 'Off by (realized)', render: (r) => (r.realizedMeanError == null ? '—' : `${r.realizedMeanError.toFixed(2)} over ${r.realizedCount}`) },
];

interface DecisionRow {
  id: string;
  object_id: string;
  field: string | null;
  output_kind: string;
  proposed_value: unknown;
  confidence: number | null;
  rationale: string;
  created_at: string;
  review_outcome: string | null;
  review_value: unknown;
  review_reason: string | null;
  reviewed_by: string | null;
}

export function LearningSection({ def, config, workspaceId, isSaving, canEdit, save, learnedRulePlaceholder }: SectionProps & { learnedRulePlaceholder: string }) {
  const addNotification = useUIStore((s) => s.addNotification);
  const agentType = def.agentType;
  const [rules, setRules] = useState<LearnedRule[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newRule, setNewRule] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [checks, setChecks] = useState<Record<string, RegressionCheck>>({});
  const [calibration, setCalibration] = useState<CalibrationRow[]>([]);
  const [rebuilding, setRebuilding] = useState(false);
  const [decisions, setDecisions] = useState<DecisionRow[]>([]);
  // The name behind each decision, from the record itself.
  const reviewerIds = useMemo(() => decisions.map((d) => d.reviewed_by), [decisions]);
  const reviewerMap = useActorDisplayMap(reviewerIds);
  const reviewerOf = (d: DecisionRow) => knownActorName(reviewerMap, d.reviewed_by);
  const gateOn = (config?.learning as { regressionGate?: boolean } | undefined)?.regressionGate ?? def.learnedRules;
  const goldSize = Number((config?.learning as { goldSetSize?: number } | undefined)?.goldSetSize ?? DEFAULT_GOLD_SET_SIZE);
  // A rule written here applies to every value this agent works on (field null).
  const firstField: string | null = null;

  const loadRules = useCallback(async () => setRules(await loadLearnedRules(workspaceId, agentType)), [workspaceId, agentType]);
  const loadCalibration = useCallback(async () => setCalibration(await loadAgentCalibration(workspaceId, agentType)), [workspaceId, agentType]);
  const loadDecisions = useCallback(async () => {
    const { data } = await getSupabaseClient()
      .from('agent_decision_records')
      .select('id, object_id, field, output_kind, proposed_value, confidence, rationale, created_at, review_outcome, review_value, review_reason, reviewed_by')
      .eq('workspace_id', workspaceId).eq('agent_type', agentType)
      .order('created_at', { ascending: false }).limit(25);
    setDecisions((data ?? []) as DecisionRow[]);
  }, [workspaceId, agentType]);

  useEffect(() => { void loadRules(); void loadCalibration(); void loadDecisions(); }, [loadRules, loadCalibration, loadDecisions]);

  const act = async (id: string, action: () => Promise<{ error: string | null }>) => {
    setBusy(id);
    try {
      const res = await action();
      if (res.error) addNotification({ type: 'error', title: 'Rule not changed', message: res.error, duration: 4000 });
      await loadRules();
    } finally {
      setBusy(null);
    }
  };

  const check = async (rule: LearnedRule) => {
    if (!config) return;
    setBusy(rule.id);
    try {
      const result = await checkRuleAgainstGoldSet({ workspaceId, agent: { agentType, name: def.name }, settings: config, ruleText: drafts[rule.id] ?? rule.ruleText, field: rule.field });
      setChecks((c) => ({ ...c, [rule.id]: result }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {def.learnedRules && (
        <SwitchGroup title="Learned rules" lead="When several corrections share a pattern, the nightly job drafts one rule in plain words. Nothing enters the prompt until a person accepts it; a revert is one click. You can write one yourself.">
          <SwitchRow
            label="Check a rule against past decisions before it is accepted"
            help={`Asks the agent again, with and without the rule, on up to ${goldSize} recent decisions people accepted. A rule that makes it agree with people less often cannot be accepted. Each check uses model calls.`}
            on={gateOn} disabled={isSaving || !canEdit}
            onChange={() => void save({ learning: { ...(config?.learning ?? {}), regressionGate: !gateOn, goldSetSize: goldSize } })}
          />
          {rules.filter((r) => r.status === 'draft').map((r) => {
            const c = checks[r.id];
            const blocked = gateOn && (!c || !c.passes);
            return (
              <div key={r.id} className="border border-[var(--mustard)] bg-[var(--mustard-dim)]/40 p-3 space-y-2">
                <p className="text-[11px] font-medium text-[var(--mustard)]">Draft · {r.field ?? 'all attributes'} · from {String((r.evidence as { corrections?: number }).corrections ?? '?')} corrections</p>
                <textarea value={drafts[r.id] ?? r.ruleText} rows={2} readOnly={!canEdit} aria-label="Draft rule"
                  onChange={(e) => { setDrafts((d) => ({ ...d, [r.id]: e.target.value })); setChecks((cs) => { const n = { ...cs }; delete n[r.id]; return n; }); }}
                  className="w-full px-2 py-1 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-[var(--mustard)] resize-y" />
                {c && (
                  <Callout tone={c.passes ? 'green' : 'coral'}>
                    {c.cases === 0
                      ? 'No past decisions to check against yet; the rule may be accepted.'
                      : `With the rule it matched people ${c.matchesWith} of ${c.cases} times; without it, ${c.matchesWithout}. ${c.passes ? 'It passes.' : 'It makes the agent agree with people less often, so it cannot be accepted.'}`}
                  </Callout>
                )}
                {canEdit && (
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" disabled={busy === r.id} onClick={() => void act(r.id, () => dismissLearnedRule(r.id))}>Dismiss</Button>
                    {gateOn && !c && (
                      <Button variant="secondary" size="sm" disabled={busy === r.id} onClick={() => void check(r)}>
                        {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Check against past decisions
                      </Button>
                    )}
                    {!blocked && (
                      <Button variant="primary" size="sm" disabled={busy === r.id} onClick={() => void act(r.id, () => approveLearnedRule(r.id, drafts[r.id]))}>
                        <Check className="h-3.5 w-3.5" /> Accept
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {rules.filter((r) => r.status === 'approved').map((r) => (
            <div key={r.id} className="flex items-start justify-between gap-3 border-t border-[var(--rule)] pt-2">
              <div>
                <p className="text-sm text-[var(--text-pri)]">{r.ruleText}</p>
                <p className="text-[11px] text-[var(--text-ter)]">v{r.version} · {r.field ?? 'all attributes'} · in the prompt{r.approvedAt ? ` since ${new Date(r.approvedAt).toLocaleDateString()}` : ''}</p>
              </div>
              {canEdit && <Button variant="ghost" size="sm" disabled={busy === r.id} onClick={() => void act(r.id, () => revertLearnedRule(r.id, 'Reverted from settings'))}>Revert</Button>}
            </div>
          ))}
          {rules.filter((r) => r.status === 'reverted').slice(0, 5).map((r) => (
            <p key={r.id} className="text-[11px] text-[var(--text-ter)] line-through border-t border-[var(--rule)] pt-2">{r.ruleText}</p>
          ))}
          {canEdit && (
            <div className="border-t border-[var(--rule)] pt-3 space-y-2">
              <textarea value={newRule} rows={2} placeholder={learnedRulePlaceholder} onChange={(e) => setNewRule(e.target.value)} aria-label="New rule"
                className="w-full px-2 py-1 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-[var(--mustard)] resize-y" />
              {newRule.trim().length >= 10 && (
                <div className="flex justify-end">
                  <Button variant="secondary" size="sm" disabled={busy === 'new'}
                    onClick={() => void act('new', async () => { const res = await draftLearnedRule({ workspaceId, agentType, field: firstField, ruleText: newRule.trim() }); if (!res.error) setNewRule(''); return res; })}>
                    <Plus className="h-3.5 w-3.5" /> Add as draft
                  </Button>
                </div>
              )}
            </div>
          )}
        </SwitchGroup>
      )}

      <SwitchGroup
        title="Track record"
        lead={`What each stated confidence has actually meant here. Once a bucket has ${MIN_REVIEWS_FOR_CALIBRATION} reviews, the confidence shown on new proposals is its accepted-unchanged rate, not the model's claim. Recomputed nightly.`}
        action={<Button variant="ghost" size="sm" disabled={rebuilding} onClick={() => { setRebuilding(true); void rebuildAgentCalibration(workspaceId, agentType).then(loadCalibration).finally(() => setRebuilding(false)); }}>{rebuilding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Recompute</Button>}
      >
        {calibration.length === 0
          ? <p className="text-xs text-[var(--text-ter)]">Nothing reviewed yet.</p>
          : <DataTable columns={CALIBRATION_COLUMNS} rows={calibration} getRowKey={(r) => `${r.field}|${r.bucket}`} density="compact" caption="Calibration by attribute and stated confidence" captionHidden />}
      </SwitchGroup>

      <SwitchGroup title="Every decision it made" lead="What it proposed or declined, with its evidence, and what people did with it." action={<Button variant="ghost" size="sm" onClick={() => void loadDecisions()}>Refresh</Button>}>
        {decisions.length === 0 ? <p className="text-xs text-[var(--text-ter)]">No decisions yet.</p> : (
          <div className="divide-y divide-[var(--rule)]">
            {decisions.map((d) => (
              <div key={d.id} className="py-2 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/prioritization/items/${d.object_id}`} className="font-medium text-[var(--text-pri)] underline">Item</Link>
                  <span className="text-[var(--text-ter)]">{d.field}</span>
                  <span className={`px-1.5 py-0.5 rounded-full ${d.output_kind.startsWith('proposal:') ? 'bg-[var(--green-dim)] text-[var(--green)]' : d.output_kind === 'abstain' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'}`}>
                    {d.output_kind.startsWith('proposal:') ? `Proposed ${proposedWords(d.proposed_value)}` : d.output_kind === 'abstain' ? 'Abstained' : d.output_kind}
                  </span>
                  {d.confidence != null && <span className="text-[var(--text-ter)]">{Math.round(d.confidence * 100)}% confidence</span>}
                  {d.review_outcome && <span className="text-[var(--text-sec)]">· {reviewOutcomeWords(d.review_outcome, reviewerOf(d))}{d.review_value != null && d.review_outcome === 'accepted_with_change' && d.output_kind === 'proposal:value' ? ` → ${String(d.review_value)}` : ''}</span>}
                  <span className="ml-auto text-[var(--text-ter)]">{new Date(d.created_at).toLocaleString()}</span>
                </div>
                <p className="text-[var(--text-ter)] italic mt-0.5">{d.rationale}</p>
                {d.review_reason && <p className="text-[var(--text-ter)] mt-0.5">Reason: {d.review_reason}</p>}
              </div>
            ))}
          </div>
        )}
      </SwitchGroup>
    </>
  );
}

export function LifecycleSection({ def }: Pick<SectionProps, 'def'>) {
  return (
    <SwitchGroup title="Version" lead={`Definition version ${def.definitionVersion}. The platform owns the definition; what changed in each version:`}>
      <ul className="space-y-1">
        {[...def.changeLog].reverse().map((c) => (
          <li key={c.version} className="text-xs text-[var(--text-sec)]"><span className="font-medium">v{c.version}</span> · {c.date} · {c.change}</li>
        ))}
      </ul>
    </SwitchGroup>
  );
}
